import { GoalCompletion, Timestamp } from '@griply/types';

export type CompletionPercentageOptions = {
  start?: number;
  end: number;
  current: number;
};

export const getCompletionPercentage = ({
  start = 0,
  end,
  current,
}: CompletionPercentageOptions) => {
  // A zero-width range is a target the goal already stood on: met once the value
  // reaches it. `0 of 0` is excluded — that is the shape every count-based goal
  // takes with nothing to measure, which is not an achievement.
  if (start === end) {
    return end !== 0 && current >= end ? 100 : 0;
  }

  const range = end - start;
  const progress = current - start;

  // Floored, so a goal that is not finished can never read 100.
  return Math.max(0, Math.min(100, Math.floor((progress / range) * 100)));
};

// A completed goal is fully achieved however it measures itself. What an unmeasurable target
// means is the caller's call: a goal shows none, a sub-goal contributes nothing to its parent.
export const getAchievedPercentage = <TUnmeasurable>(
  completedAt: Timestamp | null | undefined,
  resolveCompletion: () => GoalCompletion,
  unmeasurable: TUnmeasurable,
): number | TUnmeasurable => {
  if (completedAt) {
    return 100;
  }

  const completion = resolveCompletion();

  return completion.end === Infinity
    ? unmeasurable
    : getCompletionPercentage(completion);
};
